import React, { useState, useCallback, useEffect } from "react";
import { Grid, Button, Box, Typography, Chip } from "@mui/material";
import UserFormModal from "./creation";
import UserList from "./user_list";
import RoleChartModal from "./role_chart";
import { makeStyles } from "@mui/styles";
import userApi from "../../services/user";
import { useDispatch, useSelector } from "react-redux";
import { showLoader, hideLoader } from "../../redux/loader/actions";

const useStyles = makeStyles({
  animatedButton: {
    background: "linear-gradient(45deg, #ff416c 30%, #ff4b2b 90%)",
    color: "#fff",
    padding: "10px 30px",
    fontSize: "16px",
    fontWeight: "bold",
    textTransform: "none",
    borderRadius: "5px",
    transition: "transform 0.3s ease",
    "&:hover": {
      transform: "scale(1.05)",
      background: "linear-gradient(45deg, #ff4b2b 30%, #ff416c 90%)",
    },

    "@media (max-width: 600px)": {
      width: "100%",
      padding: "12px 4px",
      fontSize: "12px",
    },
  },
  chip: {
    marginRight: "16px",
  },
});

const UserManagement = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [users, setUsers] = useState([]);
  const [editIndex, setEditIndex] = useState(null);
  const [userFormModalOpen, setUserFormModalOpen] = useState(false);
  const [roleChartModalOpen, setRoleChartModalOpen] = useState(false);
  const [lockedRoles, setLockedRoles] = useState({});
  const [statusRoles, setStatusRoles] = useState({});
  const [rowCount, setRowCount] = useState();
  const [userList, setUserList] = useState([]);
  const menuItems = useSelector((state) => state.menuReducer.items);
  const createMenuItem = menuItems?.find(
    (item) => item.name.toLowerCase() === "users"
  );
  const isDisabled = createMenuItem?.access_type === "read_write";

  const handleAddUser = (user) => {
    if (editIndex !== null) {
      const updatedUsers = users.map((u, index) =>
        index === editIndex ? user : u
      );
      setUsers(updatedUsers);
      setEditIndex(null);
    } else {
      setUsers([...users, user]);
    }
    setUserFormModalOpen(false);
  };

  const getAllUser = useCallback(() => {
    dispatch(showLoader());
    userApi
      .getAllUser()
      .then((res) => {
        dispatch(hideLoader());
        if (res?.data?.status) {
          setUserList(res.data.data.users || []);
          setRowCount(res.data.data.pagination.totalItems || []);
        } else {
          setUserList([]);
        }
      })
      .catch((err) => {
        dispatch(hideLoader());
        console.error("Error fetching users:", err);
      });
  }, [dispatch]);

  const handleEditUser = (index) => {
    setEditIndex(index);
    setUserFormModalOpen(true);
  };

  const handleDeleteUser = (index) => {
    setUsers(users.filter((_, i) => i !== index));
  };

  const handleToggleStatus = (index) => {
    setStatusRoles((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const handleToggleLock = (index) => {
    setLockedRoles((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  return (
    <>
      <Grid item xs={12} sm={10} mb={2}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">User Management</Typography>
          <Box display="flex" alignItems="center">
            <Chip
              label="Role Chart"
              color="primary"
              variant="outlined"
              onClick={() => setRoleChartModalOpen(true)}
              className={classes.chip}
              sx={{ marginRight: "10px" }}
            />
            {isDisabled && (
              <Button
                variant="contained"
                className={classes.animatedButton}
                onClick={() => setUserFormModalOpen(true)}
              >
                Add User
              </Button>
            )}
          </Box>
        </Box>
      </Grid>
      <UserList
        onEditUser={handleEditUser}
        onDeleteUser={handleDeleteUser}
        lockedRoles={lockedRoles}
        handleToggleStatus={handleToggleStatus}
        handleToggleLock={handleToggleLock}
        rowCount={rowCount}
        userList={userList}
        setUserList={setUserList}
      />
      <UserFormModal
        open={userFormModalOpen}
        onClose={() => setUserFormModalOpen(false)}
        onSave={handleAddUser}
        existingUser={editIndex !== null ? users[editIndex] : null}
        getAllUser={getAllUser}
      />
      <RoleChartModal
        open={roleChartModalOpen}
        onClose={() => setRoleChartModalOpen(false)}
      />
    </>
  );
};

export default UserManagement;
